// Voice note recorder for CW staff notes — records in the browser, the server
// transcribes (Whisper) and tidies the text (Gemini Flash); result goes into the
// note box for the staff member to check before sending.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic, Square } from "lucide-react";
import api from "../../../lib/api";
import { useTheme } from "../../../context/ThemeContext";

export default function VoiceNoteButton({ quoteId, onText, onResult, url, testid = "staff-note-voice", label = "Voice", title }) {
  const light = useTheme().theme === "light";
  const [state, setState] = useState("idle"); // idle | rec | busy
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("This browser can't record audio"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        setState("busy");
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice-note.${ext}`);
          const { data } = await api.post(url || `/quotes/${quoteId}/voice-note`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
          if (onResult) await onResult(data);
          else { onText(data.clean || data.raw || ""); toast.success("Voice note transcribed — check it, then send"); }
        } catch (e) { toast.error(e?.response?.data?.detail || "Transcription failed"); }
        finally { setState("idle"); setSecs(0); }
      };
      recRef.current = rec;
      rec.start();
      setState("rec"); setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => {
        if (s >= 299) { rec.state === "recording" && rec.stop(); }
        return s + 1;
      }), 1000);
    } catch { toast.error("Microphone permission denied"); }
  };
  const stop = () => recRef.current?.state === "recording" && recRef.current.stop();

  const rec = state === "rec";
  return (
    <button type="button" onClick={rec ? stop : start} disabled={state === "busy" || (!quoteId && !url)} data-testid={testid}
      title={rec ? "Stop and transcribe" : (title || "Record a voice note — AI types and tidies it for you")}
      className={rec ? "qb-rec-pulse" : ""}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "0 10px", borderRadius: 8, cursor: "pointer", flexShrink: 0, fontSize: 11, fontWeight: 800, fontFamily: "var(--t2-mono)",
        border: `1px solid ${rec ? "#f43f5e" : light ? "#CBD5E1" : "rgba(255,255,255,.18)"}`, background: rec ? "rgba(244,63,94,0.15)" : light ? "#FFFFFF" : "transparent", color: rec ? (light ? "#BE123C" : "#fda4af") : light ? "#1E293B" : "#E2E8F0" }}>
      {state === "busy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : rec ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
      {state === "busy" ? "Typing…" : rec ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}` : label}
    </button>
  );
}
